import { Box, LinearProgress, Typography } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { formatDuration } from 'helpers/bulkEdit/changePlan';
import { BulkRunProgress, BulkRunResult } from 'helpers/bulkEdit/runEngine';
import React from 'react';

/** Progress and outcome rendering shared by the bulk edit and import dialogs. */

interface ProgressProps {
	title: string;
	progress: BulkRunProgress | null;
	cancelling: boolean;
}

export function RunProgressView({ title, progress, cancelling }: ProgressProps) {
	let completed = progress?.completed ?? 0;
	let total = progress?.total ?? 0;
	let percent = total ? Math.min((completed / total) * 100, 100) : 0;

	return (
		<>
			<Typography variant="subtitle1" gutterBottom>
				{title}
			</Typography>
			<LinearProgress variant="determinate" value={percent} />
			<Box mt={2}>
				<Typography variant="body2">
					{completed} of {total} records
				</Typography>
				<Typography variant="body2" color="textSecondary">
					Elapsed {formatDuration(progress?.elapsedMs)} · Remaining {formatDuration(progress?.etaMs)}
				</Typography>
			</Box>
			{cancelling && (
				<Typography variant="body2" color="error">
					Stopping after the current batch…
				</Typography>
			)}
		</>
	);
}

interface ResultProps {
	result: BulkRunResult;
	isUndo: boolean;
	/** Records the run deliberately left alone, so the totals add up for the reader. */
	skippedCount?: number;
	completeTitle: string;
}

export function RunResultAlert({ result, isUndo, skippedCount, completeTitle }: ResultProps) {
	return (
		<Alert severity={result.failed.length ? 'warning' : 'success'}>
			<AlertTitle>{isUndo ? 'Undo complete' : result.cancelled ? 'Stopped early' : completeTitle}</AlertTitle>
			{result.succeeded.length} record{result.succeeded.length === 1 ? '' : 's'} updated in {formatDuration(result.elapsedMs)}
			{!isUndo && skippedCount ? `, ${skippedCount} left unchanged` : ''}
			{result.failed.length ? `, ${result.failed.length} failed` : ''}.
			{result.failed.length ? ' The failures are marked in the list below.' : ''}
		</Alert>
	);
}
